// src/components/DeckBuilder.tsx - Enhanced with deck locking system

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ddApi } from '../lib/supabaseClient';
import { deckApi } from '../lib/deckApi';
import { Card, PlayerDeck } from '../types/index';
import CardDisplay from './CardDisplay';
import SynergySuggestions from './SynergySuggestions';
import '../styles/DeckBuilder.css';

const DeckBuilder: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // State management
  const [playerCards, setPlayerCards] = useState<Card[]>([]);
  const [allCards, setAllCards] = useState<Card[]>([]);
  const [playerDecks, setPlayerDecks] = useState<PlayerDeck[]>([]);
  const [selectedDeck, setSelectedDeck] = useState<1 | 2 | 3>(1);
  const [currentDeckCards, setCurrentDeckCards] = useState<Card[]>([]);
  const [currentDeckName, setCurrentDeckName] = useState<string>('Deck 1');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info' | 'warning', text: string } | null>(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'offensive' | 'defensive' | 'utility'>('all');
  const [rarityFilter, setRarityFilter] = useState<'all' | 'common' | 'rare' | 'legendary'>('all');

  useEffect(() => {
    if (!user) return;
    loadData();
  }, [user]);

  useEffect(() => {
    // Load selected deck when deck selection changes
    const deck = playerDecks.find(d => d.deck_slot === selectedDeck);
    if (deck) {
      setCurrentDeckCards(deck.cards || []);
      setCurrentDeckName(deck.deck_name);
    }
  }, [selectedDeck, playerDecks]);

  const loadData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Load player's owned cards, existing decks (with lock status), AND all cards for synergy suggestions
      const [playerCardsData, decksData, allCardsData] = await Promise.all([
        ddApi.getPlayerCards(user.id),
        deckApi.getPlayerDecks(user.id), // Now includes lock status
        ddApi.getAllCards()
      ]);

      setPlayerCards(playerCardsData.map(pc => pc.card!));
      setAllCards(allCardsData);
      
      // Initialize decks if they don't exist
      if (decksData.length === 0) {
        await deckApi.initializePlayerDecks(user.id);
        const newDecksData = await deckApi.getPlayerDecks(user.id);
        setPlayerDecks(newDecksData);
      } else {
        setPlayerDecks(decksData);
      }

    } catch (error) {
      console.error('Error loading deck builder data:', error);
      setMessage({ type: 'error', text: 'Failed to load deck data' });
    } finally {
      setLoading(false);
    }
  };

  // Check if currently selected deck is locked
  const getSelectedDeckStatus = () => {
    const deck = playerDecks.find(d => d.deck_slot === selectedDeck);
    return {
      isLocked: deck?.isLocked || false,
      lockInfo: deck?.lockInfo || []
    };
  };

  const saveDeck = async () => {
    if (!user || saving) return;
    
    const deckStatus = getSelectedDeckStatus();
    if (deckStatus.isLocked) {
      const gameList = deckStatus.lockInfo.map(g => `vs ${g.opponent} (${g.status})`).join(', ');
      setMessage({ 
        type: 'error', 
        text: `Cannot save - Deck ${selectedDeck} is locked. Currently used in: ${gameList}` 
      });
      setTimeout(() => setMessage(null), 5000);
      return;
    }
    
    try {
      setSaving(true);
      setMessage(null);

      const cardIds = currentDeckCards.map(card => card.id);
      
      if (cardIds.length > 0 && cardIds.length < 5) {
        setMessage({ 
          type: 'error', 
          text: 'Deck needs at least 5 cards for competitive play. Current: ' + cardIds.length 
        });
        setSaving(false);
        setTimeout(() => setMessage(null), 3000);
        return;
      }

      const result = await deckApi.saveDeck(user.id, selectedDeck, currentDeckName, cardIds);

      if (result.success) {
        if (cardIds.length === 0) {
          setMessage({ type: 'info', text: 'Deck cleared successfully!' });
        } else if (cardIds.length < 5) {
          setMessage({ 
            type: 'info', 
            text: `Deck saved with ${cardIds.length} cards. Add ${5 - cardIds.length} more for competitive play.` 
          });
        } else {
          setMessage({ type: 'success', text: 'Deck saved successfully!' });
        }
        
        // Reload deck data to get updated lock status
        const updatedDecks = await deckApi.getPlayerDecks(user.id);
        setPlayerDecks(updatedDecks);
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to save deck' });
      }

    } catch (error) {
      console.error('Error saving deck:', error);
      setMessage({ type: 'error', text: 'Server error while saving deck' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const addCardToDeck = (card: Card) => {
    const deckStatus = getSelectedDeckStatus();
    if (deckStatus.isLocked) {
      setMessage({ 
        type: 'error', 
        text: `Cannot modify locked deck - currently used in active games` 
      });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    if (currentDeckCards.length >= 10) {
      setMessage({ type: 'error', text: 'Deck is full (maximum 10 cards)' });
      setTimeout(() => setMessage(null), 2000);
      return;
    }

    if (currentDeckCards.some(c => c.id === card.id)) {
      setMessage({ type: 'error', text: 'Card already in deck' });
      setTimeout(() => setMessage(null), 2000);
      return;
    }

    setCurrentDeckCards([...currentDeckCards, card]);
  };

  const removeCardFromDeck = (cardId: number) => {
    const deckStatus = getSelectedDeckStatus();
    if (deckStatus.isLocked) {
      setMessage({ 
        type: 'error', 
        text: `Cannot modify locked deck - currently used in active games` 
      });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    setCurrentDeckCards(currentDeckCards.filter(card => card.id !== cardId));
  };

  const clearDeck = async () => {
    if (!user) return;
    
    const deckStatus = getSelectedDeckStatus();
    if (deckStatus.isLocked) {
      const gameList = deckStatus.lockInfo.map(g => `vs ${g.opponent} (${g.status})`).join(', ');
      setMessage({ 
        type: 'error', 
        text: `Cannot clear - Deck ${selectedDeck} is locked. Currently used in: ${gameList}` 
      });
      setTimeout(() => setMessage(null), 5000);
      return;
    }
    
    if (window.confirm(`Clear all cards from ${currentDeckName}?`)) {
      const result = await deckApi.clearDeck(user.id, selectedDeck);
      if (result.success) {
        setCurrentDeckCards([]);
        setMessage({ type: 'info', text: 'Deck cleared' });
        setTimeout(() => setMessage(null), 2000);
        
        // Reload deck data
        const updatedDecks = await deckApi.getPlayerDecks(user.id);
        setPlayerDecks(updatedDecks);
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to clear deck' });
        setTimeout(() => setMessage(null), 3000);
      }
    }
  };

  const handleSynergyCardSelect = (card: Card) => {
    addCardToDeck(card);
  };

  const handleShopRedirect = () => {
    navigate('/dashboard');
    setMessage({ type: 'info', text: 'Check the Card Shop tab to purchase suggested cards!' });
  };

  // Filter available cards
  const getFilteredCards = (): Card[] => {
    let filtered = playerCards.filter(card => 
      !currentDeckCards.some(deckCard => deckCard.id === card.id)
    );

    if (searchTerm) {
      filtered = filtered.filter(card =>
        card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        card.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(card => card.card_type === typeFilter);
    }

    if (rarityFilter !== 'all') {
      filtered = filtered.filter(card => card.rarity === rarityFilter);
    }

    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  };

  const filteredCards = getFilteredCards();
  const selectedDeckStatus = getSelectedDeckStatus();

  if (loading) {
    return (
      <div className="deck-builder-loading">
        <div className="loading-spinner">Loading deck builder...</div>
      </div>
    );
  }

  return (
    <div className="deck-builder">
      {/* Header */}
      <header className="deck-builder-header">
        <div className="header-content">
          <div>
            <h1 className="title">🃏 Deck Builder</h1>
            <p className="subtitle">Build your perfect strategy</p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="back-button"
          >
            Back to Dashboard
          </button>
        </div>
      </header>

      {/* Message Display */}
      {message && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Main Content - Simplified Two Column Layout */}
      <div className="deck-builder-content-simple">
        
        {/* LEFT COLUMN: Deck Management */}
        <div className="deck-management-panel-simple">
          
          {/* Deck Selection - Enhanced with Lock Status */}
          <div className="deck-selector-simple">
            <h3>Select Deck to Edit</h3>
            <div className="deck-tabs-simple">
              {[1, 2, 3].map(deckNum => {
                const deck = playerDecks.find(d => d.deck_slot === deckNum);
                const cardCount = deck?.cards?.length || 0;
                const isLocked = deck?.isLocked || false;
                const lockInfo = deck?.lockInfo || [];
                
                return (
                  <button
                    key={deckNum}
                    onClick={() => setSelectedDeck(deckNum as 1 | 2 | 3)}
                    className={`deck-tab-simple ${selectedDeck === deckNum ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
                    title={isLocked ? `Locked - used in: ${lockInfo.map(g => `vs ${g.opponent}`).join(', ')}` : ''}
                  >
                    <div className="deck-tab-name-simple">
                      {isLocked && <span className="lock-icon">🔒</span>}
                      {deck?.deck_name || `Deck ${deckNum}`}
                    </div>
                    <div className="deck-tab-count-simple">{cardCount}/10</div>
                    {isLocked && (
                      <div className="lock-status-simple">
                        <span className="lock-text">LOCKED</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Lock Warning for Selected Deck */}
          {selectedDeckStatus.isLocked && (
            <div className="lock-warning-simple">
              <div className="lock-warning-content">
                <span className="lock-warning-icon">🔒</span>
                <div className="lock-warning-text">
                  <strong>Deck {selectedDeck} is locked</strong>
                  <div className="lock-warning-details">
                    Currently used in:
                    {selectedDeckStatus.lockInfo.map((game, idx) => (
                      <div key={idx} className="active-game-info">
                        • vs {game.opponent} ({game.status})
                      </div>
                    ))}
                  </div>
                  <div className="lock-warning-note">
                    Deck will unlock when these games are completed or abandoned.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Deck Name and Actions */}
          <div className="deck-header-simple">
            <input
              type="text"
              value={currentDeckName}
              onChange={(e) => setCurrentDeckName(e.target.value)}
              className="deck-name-input-simple"
              placeholder="Deck Name"
              maxLength={50}
              disabled={selectedDeckStatus.isLocked}
            />
            <div className="deck-actions-simple">
              <button
                onClick={clearDeck}
                className="clear-button-simple"
                disabled={currentDeckCards.length === 0 || selectedDeckStatus.isLocked}
                title={selectedDeckStatus.isLocked ? 'Cannot clear locked deck' : 'Clear all cards'}
              >
                Clear
              </button>
              <button
                onClick={saveDeck}
                className="save-button-simple"
                disabled={saving || selectedDeckStatus.isLocked}
                title={selectedDeckStatus.isLocked ? 'Cannot save locked deck' : 'Save deck'}
              >
                {saving ? 'Saving...' : 'Save Deck'}
              </button>
            </div>
          </div>

          {/* Current Deck Cards */}
          <div className="current-deck-simple">
            <h4>Current Deck ({currentDeckCards.length}/10)</h4>
            {currentDeckCards.length === 0 ? (
              <div className="empty-deck-simple">
                <p>No cards in deck</p>
                {!selectedDeckStatus.isLocked && (
                  <p className="hint">Add cards from your collection on the right</p>
                )}
              </div>
            ) : (
              <div className="deck-cards-grid-simple">
                {currentDeckCards.map(card => (
                  <div key={card.id} className="deck-card-slot-simple">
                    <CardDisplay
                      card={card}
                      compact={false}
                      showDetails={false}
                    />
                    {!selectedDeckStatus.isLocked && (
                      <button
                        onClick={() => removeCardFromDeck(card.id)}
                        className="remove-card-button-simple"
                        title="Remove from deck"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                
                {/* Empty slots */}
                {Array.from({ length: 10 - currentDeckCards.length }).map((_, index) => (
                  <div key={`empty-${index}`} className="deck-card-slot-simple empty">
                    <div className="empty-slot-simple">
                      <span>Empty</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: Available Cards */}
        <div className="available-cards-panel-simple">
          
          {/* Filters */}
          <div className="filters-section-simple">
            <h3>Your Card Collection</h3>
            <div className="filters-simple">
              <input
                type="text"
                placeholder="Search cards..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input-simple"
              />
              
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as any)}
                className="filter-select-simple"
              >
                <option value="all">All Types</option>
                <option value="offensive">⚔️ Offensive</option>
                <option value="defensive">🛡️ Defensive</option>
                <option value="utility">🔧 Utility</option>
              </select>
              
              <select
                value={rarityFilter}
                onChange={(e) => setRarityFilter(e.target.value as any)}
                className="filter-select-simple"
              >
                <option value="all">All Rarities</option>
                <option value="common">Common</option>
                <option value="rare">Rare</option>
                <option value="legendary">Legendary</option>
              </select>
            </div>
          </div>

          {/* Available Cards Grid - Fewer but Larger Cards */}
          <div className="available-cards-simple">
            <div className="cards-info-simple">
              <p>Showing {filteredCards.length} available cards</p>
              {selectedDeckStatus.isLocked && (
                <p className="lock-info-simple">
                  🔒 Deck is locked - cannot add/remove cards
                </p>
              )}
            </div>
            
            {filteredCards.length === 0 ? (
              <div className="no-cards-simple">
                <p>No cards match your filters</p>
              </div>
            ) : (
              <div className="cards-grid-simple">
                {filteredCards.map(card => (
                  <div key={card.id} className="available-card-simple">
                    <CardDisplay
                      card={card}
                      showDetails={true}
                    />
                    <button
                      onClick={() => addCardToDeck(card)}
                      className="add-card-button-simple"
                      disabled={currentDeckCards.length >= 10 || selectedDeckStatus.isLocked}
                      title={
                        selectedDeckStatus.isLocked ? 'Cannot modify locked deck' :
                        currentDeckCards.length >= 10 ? 'Deck is full' : 'Add to deck'
                      }
                    >
                      {selectedDeckStatus.isLocked ? '🔒 Locked' :
                       currentDeckCards.length >= 10 ? 'Deck Full' : 'Add to Deck'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* BOTTOM: Synergy Suggestions - Full Width */}
      {currentDeckCards.length > 0 && !selectedDeckStatus.isLocked && (
        <div className="synergy-section-bottom">
          <SynergySuggestions
            currentDeckCards={currentDeckCards}
            playerCards={playerCards}
            allCards={allCards}
            onCardSelect={handleSynergyCardSelect}
            onShopRedirect={handleShopRedirect}
          />
        </div>
      )}

      {/* Lock Info Footer */}
      {selectedDeckStatus.isLocked && (
        <div className="lock-info-footer">
          <div className="lock-info-content">
            <h4>Why is my deck locked?</h4>
            <p>
              Decks are automatically locked when they're being used in active or pending games. 
              This prevents players from changing their deck composition mid-game, which would be unfair.
            </p>
            <p>
              Your deck will automatically unlock when all games using it are completed or abandoned.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeckBuilder;